import type { EffectiveFeatures } from './user-features-api';

/** Nothing granted. The answer while the features are still unknown, so a row is drawn once it
 *  is known to be reachable rather than drawn and then withdrawn. */
export function emptyFeatures(): EffectiveFeatures {
  return { allow: [], deny: [], overrides: { allow: [], deny: [] } };
}

function readGuidList(source: unknown): string[] {
  let list: string[] = [];
  if (Array.isArray(source)) {
    list = source.filter((guid): guid is string => typeof guid === 'string');
  }
  return list;
}

/** Validated rather than trusted: a stale or malformed seed degrades to nothing granted and the
 *  fetch fills it in, instead of drawing navigation off whatever happened to be in the page. */
export function readFeatures(source: unknown): EffectiveFeatures | null {
  if (typeof source !== 'object' || source === null) {
    return null;
  }
  const seed = source as { allow?: unknown; deny?: unknown; overrides?: { allow?: unknown; deny?: unknown } };
  if (!Array.isArray(seed.allow) || !Array.isArray(seed.deny)) {
    return null;
  }
  let overrides = { allow: [] as string[], deny: [] as string[] };
  if (typeof seed.overrides === 'object' && seed.overrides !== null) {
    overrides = { allow: readGuidList(seed.overrides.allow), deny: readGuidList(seed.overrides.deny) };
  }
  return { allow: readGuidList(seed.allow), deny: readGuidList(seed.deny), overrides: overrides };
}

/** What the server stamped onto the SPA root, which is the only channel SpaShell offers. */
export function readFeaturesFromDocument(): EffectiveFeatures | null {
  const attribute = document.getElementById('root')?.dataset.features;
  if (typeof attribute === 'string' && attribute.length > 0) {
    try {
      return readFeatures(JSON.parse(attribute));
    } catch {
      return null;
    }
  }
  return null;
}
